import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import style from './index.scss';
import { Table, Collapse, Tooltip } from 'antd';
import { TextComponent } from '../../../trm/components/TableComponents';
import { CustomHeader } from '../../../trm/components/CustomHeaderComponent/customHeader';
import { SIMILAR_KYC_DOWNLOAD_HEADER } from './constants';
import { CSVLink } from 'react-csv';
import classNames from 'classnames';

function SimilarKYCCustomer({
  commonKYCCustomers,
  commonKYCCustomersDownload,
  customerId,
  getCommonKYCcustomersData,
  getCommonKYCcustomersDownloadData,
  alertId,
  collapseMode
}) {
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const { Panel } = Collapse;
  const data = commonKYCCustomers?.data?.customers;
  const dataSource = data;
  const csvDataSource = commonKYCCustomersDownload?.data?.customers;
  const scrollOptions = { y: 400 }; // Adjust the height value as needed
  const [similarKycFields, setSimilarKycFields] = useState([]);
  const [similarKycInfo, setSimilarKycInfo] = useState([]);
  const [csvData, setCSVData] = useState();
  const [combinedData, setCombinedData] = useState([]);
  const [disabledCSV, setDisabledCSV] = useState();
  const [csvFetched, setCsvFetched] = useState(false);
  const totalRecords = commonKYCCustomers?.data?.totalCount;
  useEffect(() => {
    if (alertId && customerId) {
      getCommonKYCcustomersData({
        alertId: alertId,
        customerId: customerId,
        limit: limit,
        offset: offset
      });
    }
  }, [customerId, , limit, offset]);

  useEffect(() => {
    if (commonKYCCustomersDownload?.data !== null || undefined) {
      setDisabledCSV(false);
    } else {
      setDisabledCSV(true);
    }
  }, [commonKYCCustomersDownload]);

  useEffect(() => {
    if (csvDataSource) {
      const updatedSimilarKycFields = [];
      const updatedSimilarKycInfo = [];
      csvDataSource.forEach((data) => {
        const parsedFields = JSON.parse(data.similarKycFields);
        const parsedInfo = JSON.parse(data.similarKycInfo);
        updatedSimilarKycFields.push(parsedFields);
        updatedSimilarKycInfo.push(parsedInfo);
      });
      setSimilarKycFields(updatedSimilarKycFields);
      setSimilarKycInfo(updatedSimilarKycInfo);
    }
  }, [dataSource]);

  useEffect(() => {
    if (totalRecords) {
      getCommonKYCcustomersDownloadData({
        alertId: alertId,
        customerId: customerId,
        limit: commonKYCCustomers?.data?.totalCount,
        offset: offset
      });
    }
  }, [totalRecords]);

  useEffect(() => {
    if (commonKYCCustomersDownload?.data?.customers) {
      const combinedData = commonKYCCustomersDownload.data.customers.map(
        (item) => ({
          similarKycFields: JSON.parse(item.similarKycFields),
          similarKycInfo: JSON.parse(item.similarKycInfo),
          partyKey: item.similarKycPartyKey
        })
      );
      setCombinedData(combinedData);

      if (combinedData) {
        const formattedData = combinedData.map((item) => {
          const formattedItem = { ...item };
          // Format the 'Age' field
          formattedItem.similarKycInfo.PARTY_AGE = Math.floor(
            parseFloat(item.similarKycInfo.PARTY_AGE)
          );
          return formattedItem;
        });
        // Download CSV using the formatted data
        setCSVData(formattedData);
        // Format the data manually before downloading CSV
      }
    }
  }, [commonKYCCustomersDownload, csvFetched]);

  useEffect(() => {
    if (
      similarKycInfo != null ||
      (undefined && similarKycFields != null) ||
      undefined
    ) {
      setCSVData({ similarKycFields, similarKycInfo });
      const combinedData = [];
      for (let i = 0; i < similarKycFields.length; i++) {
        const fields = similarKycFields[i];
        const info = similarKycInfo[i];

        // Update the customer type value in the combined object
        if (info.INDIVIDUAL_CORPORATE_TYPE) {
          if (info.INDIVIDUAL_CORPORATE_TYPE === 'I') {
            info.INDIVIDUAL_CORPORATE_TYPE = 'Individual';
          } else if (info.INDIVIDUAL_CORPORATE_TYPE === 'C') {
            info.INDIVIDUAL_CORPORATE_TYPE = 'Corporate';
          }
        }

        const combinedObject = {
          similarKycFields: fields,
          similarKycInfo: info
        };
        combinedData.push(combinedObject);
      }
      setCombinedData(combinedData);
    }
  }, [similarKycFields, similarKycInfo]);

  const handlePaginationChange = (page, pageSize) => {
    setLimit(pageSize);
    setOffset((page - 1) * pageSize);
  };

  const paginationOptions = {
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'], // Add more options as needed
    defaultPageSize: 10,
    scroll: { scrollOptions },
    onChange: handlePaginationChange,
    showLessItems: true, // Display fewer page numbers
    pageSize: limit,
    current: Math.floor(offset / limit) + 1,
    total: totalRecords,
    showEllipsis: true, // Show ellipsis (...) for hidden page numbers
    itemRender: (current, type, originalElement) => {
      if (type === 'prev' || type === 'next') {
        return originalElement;
      }
      if (type === 'page') {
        return (
          <a href="#" onClick={() => handlePaginationChange(current, limit)}>
            {current}
          </a>
        );
      }
      return originalElement;
    }
  };

  const columns = [
    {
      title: <CustomHeader title="Customer ID" />,
      className: style.bgWhite,
      width: '3%',
      render: (record) => {
        return (
          <TextComponent
            style={{ color: '#748AA1' }}
            id={record.similarKycPartyKey}
          />
        );
      }
    },
    {
      title: <CustomHeader title="Customer Name" />,
      className: style.bgWhite,
      width: '6%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let Name = '-';
        if (parsedData.PARTY_NAME) {
          Name = parsedData.PARTY_NAME;
        }
        return <TextComponent style={{ color: '#748AA1' }} id={Name} />;
      }
    },
    {
      title: <CustomHeader title="Customer Type" />,
      className: style.bgWhite,
      width: '5%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let customerType = '-';
        if (parsedData.INDIVIDUAL_CORPORATE_TYPE) {
          if (parsedData.INDIVIDUAL_CORPORATE_TYPE === 'I') {
            customerType = 'Individual';
          } else if (parsedData.INDIVIDUAL_CORPORATE_TYPE === 'C') {
            customerType = 'Corporate';
          }
        }
        return <TextComponent style={{ color: '#748AA1' }} id={customerType} />;
      }
    },
    {
      title: <CustomHeader title="Customer ID No" />,
      className: style.bgWhite,
      width: '3%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let CUSTOMER_ID_NO = '-';
        if (parsedData.CUSTOMER_ID_NO) {
          CUSTOMER_ID_NO = parsedData.CUSTOMER_ID_NO;
        }
        return (
          <TextComponent style={{ color: '#748AA1' }} id={CUSTOMER_ID_NO} />
        );
      }
    },
    {
      title: <CustomHeader title="Email Address" />,
      className: style.bgWhite,
      width: '6%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let email = '-';
        if (parsedData.KYC_EMAIL_ADDRESS) {
          email = parsedData.KYC_EMAIL_ADDRESS;
        }

        if (email.length > 20 && email.length > 0) {
          return (
            <Tooltip title={email}>
              <p style={{ color: '#747F8A', marginTop: '10px' }}>
                {email.substring(0, 20) + '...'}
              </p>
            </Tooltip>
          );
        } else {
          return <TextComponent style={{ color: '#748AA1' }} id={email} />;
        }
      }
    },
    {
      title: <CustomHeader title="Age" />,
      className: style.bgWhite,
      width: '3%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let Age = '-';
        if (parsedData.PARTY_AGE) {
          Age = Math.floor(parsedData.PARTY_AGE);
        }
        return <TextComponent style={{ color: '#748AA1' }} id={Age} />;
      }
    },
    {
      title: <CustomHeader title="Risk Status" />,
      className: style.bgWhite,
      width: '5%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let riskLevel = '-';
        if (parsedData.RISK_LEVEL) {
          riskLevel = parsedData.RISK_LEVEL;
        }
        return <TextComponent style={{ color: '#748AA1' }} id={riskLevel} />;
      }
    },
    {
      title: <CustomHeader title="Occupation" />,
      className: style.bgWhite,
      width: '6%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let Occupation = '-';
        if (parsedData.OCCUPATION) {
          Occupation = parsedData.OCCUPATION;
        }
        return <TextComponent style={{ color: '#748AA1' }} id={Occupation} />;
      }
    },
    {
      title: <CustomHeader title="Mailing Address" />,
      className: style.bgWhite,
      width: '7%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let maiLAddress = '-';
        if (parsedData.KYC_MAILING_ADDRESS) {
          maiLAddress = parsedData.KYC_MAILING_ADDRESS;
        }

        if (maiLAddress.length > 20 && maiLAddress.length > 0) {
          return (
            <Tooltip title={maiLAddress}>
              <p style={{ color: '#747F8A', marginTop: '10px' }}>
                {maiLAddress.substring(0, 20) + '...'}
              </p>
            </Tooltip>
          );
        } else {
          return (
            <TextComponent style={{ color: '#748AA1' }} id={maiLAddress} />
          );
        }
      }
    },
    {
      title: <CustomHeader title="Residence address" />,
      className: style.bgWhite,
      width: '5%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let residenceAddress = '-';
        if (parsedData.RESIDENTIAL_ADDRESS) {
          residenceAddress = parsedData.RESIDENTIAL_ADDRESS;
        }
        if (residenceAddress.length > 25 && residenceAddress.length > 0) {
          return (
            <Tooltip title={residenceAddress}>
              {/* <TextComponent style={{ color: '#748AA1' }} id={} /> */}
              <p style={{ color: '#747F8A', marginTop: '10px' }}>
                {residenceAddress.substring(0, 25) + '...'}
              </p>
            </Tooltip>
          );
        } else {
          return (
            <TextComponent style={{ color: '#748AA1' }} id={residenceAddress} />
          );
        }
      }
    },
    {
      title: <CustomHeader title="Country of Residence" />, //tbf
      className: style.bgWhite,
      width: '5%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let Country = '-';
        if (parsedData.RESIDENCE_OPERATION_COUNTRY) {
          Country = parsedData.RESIDENCE_OPERATION_COUNTRY;
        }
        return <TextComponent style={{ color: '#748AA1' }} id={Country} />;
      }
    },
    {
      title: <CustomHeader title="Contact Number" />,
      className: style.bgWhite,
      width: '1%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let Contact = '-';
        if (parsedData.CUSTOMER_CONTACT_NO) {
          Contact = parsedData.CUSTOMER_CONTACT_NO;
        }
        return <TextComponent style={{ color: '#748AA1' }} id={Contact} />;
      }
    },
    {
      title: <CustomHeader title="Authorized signatory details" />,
      className: style.bgWhite,
      width: '9%',
      render: (record) => {
        let parsedData = JSON.parse(record.similarKycInfo);
        let AUTHORIZED = '-';
        if (parsedData.KYC_AUTHORISED_SIGNATORY_PERSON) {
          AUTHORIZED = parsedData.KYC_AUTHORISED_SIGNATORY_PERSON;
        }
        return <TextComponent style={{ color: '#748AA1' }} id={AUTHORIZED} />;
      }
    },
    {
      title: <CustomHeader title="Duplicate With" />,
      className: style.bgWhite,
      width: '5%',
      render: (record) => {
        let similarInfo = JSON.parse(record.similarKycFields);
        return similarInfo.map((info) => {
          return <TextComponent style={{ color: '#748AA1' }} id={info} />;
        });
      }
    }
  ];

  const tableDataSource = useMemo(() => {
    return dataSource?.map((item, index) => ({ ...item, key: index }));
  }, [dataSource]);

  return (
    <>
      {collapseMode ? (
        <KYCTablePanel>
          <Collapse
            ghost
            bordered={false}
            defaultActiveKey={['1']}
            expandIconPosition="right">
            <Panel
              header={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '5px 0 5px 0',
                    width: '100%',
                    alignItems: 'center'
                  }}>
                  <KYCTableTitle>
                    Customer with Similar KYC (
                    {commonKYCCustomers?.data?.totalCount})
                  </KYCTableTitle>
                  <DownloadCSVWrapper>
                    {disabledCSV ? (
                      ''
                    ) : (
                      <CSVLink
                        data={combinedData}
                        headers={SIMILAR_KYC_DOWNLOAD_HEADER}
                        filename="SIMILAR_KYC_ACCOUNTS.csv"
                        onClick={(e) => e.stopPropagation()}>
                        <DownloadIcon
                          className={classNames(
                            'icon20',
                            'icon20_icon_download'
                          )}
                        />
                      </CSVLink>
                    )}
                  </DownloadCSVWrapper>
                </div>
              }
              key="1">
              <TableAreaPanel>
                <Table
                  rowKey="key"
                  className={style.kycTable}
                  columns={columns}
                  dataSource={tableDataSource}
                  scroll={{ x: 240 }}
                  loading={false}
                  pagination={paginationOptions}
                />
              </TableAreaPanel>
            </Panel>
          </Collapse>
        </KYCTablePanel>
      ) : (
        <KYCTablePanel>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '5px 0 5px 0'
            }}>
            <KYCTableTitle style={{ paddingBottom: '10px' }}>
              Customer with Similar KYC ({commonKYCCustomers?.data?.totalCount})
            </KYCTableTitle>
            <DownloadCSVWrapper>
              {disabledCSV ? (
                ''
              ) : (
                <CSVLink
                  data={combinedData}
                  headers={SIMILAR_KYC_DOWNLOAD_HEADER}
                  filename="SIMILAR_KYC_ACCOUNTS.csv"
                  onClick={(e) => e.stopPropagation()}>
                  <DownloadIcon
                    className={classNames('icon20', 'icon20_icon_download')}
                  />
                </CSVLink>
              )}
            </DownloadCSVWrapper>
          </div>

          <TableAreaPanel>
            <Table
              className={style.kycTable}
              columns={columns}
              dataSource={tableDataSource}
              scroll={{ x: 240 }}
              loading={false}
              rowKey="key"
              pagination={paginationOptions}
            />
          </TableAreaPanel>
        </KYCTablePanel>
      )}
    </>
  );
}

export const KYCTablePanel = styled.div`
  background: #fff;
  border-radius: 5px;
  padding: 15px;
  margin-top: 16px;
  box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.25);

  .ant-collapse-header {
    display: flex;
    align-items: center;
    padding-left: 0 !important;
  }

  .ant-pagination {
    .ant-pagination-item {
      color: #00000;
      border-radius: 4px;
    }
    .ant-pagination-item-active {
      color: #00000 !important;
      border-color: #1890ff;
    }
    .ant-pagination-total-text {
      color: #b8bec5;
      line-height: 2rem;
    }
    .ant-pagination-item-link {
      border-radius: 4px;
    }
    .anticon {
      vertical-align: text-top;
    }
  }
`;

export const KYCTableTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
`;

export const TableAreaPanel = styled.div`
margin:20px 0 20px 0:
`;

export const DownloadCSVWrapper = styled.div`
  float: right;
  margin-right: 16px;
`;

export const DownloadIcon = styled.i`
  font-size: 22px !important;
  margin-top: 2px;
`;

export default SimilarKYCCustomer;
